import { Controller } from "@hotwired/stimulus"

// AI Fridge Tool (Phase 1 item 1.2). Picker → POST /fridge/suggest →
// render suggestion cards by cloning the <template> in the page.
//
// All visual state is the chips' data-selected attribute and a small
// internal Map of slug → name. Submit POSTs the selected list, parses
// the JSON response, and renders. Loading / error / rate-limit /
// killswitch all surface in a small banner area.
export default class extends Controller {
  static targets = [
    "search", "chip", "chipGrid",
    "selected", "selectedEmpty", "counter",
    "submit",
    "dietSelect",
    "results", "suggestions",
    "loading", "banner"
  ]

  static values = { submitUrl: String }

  // Bump this if the persisted shape changes.
  static STORAGE_KEY = "breakfast.fridge.selected"

  connect() {
    this._selected = new Map()
    this._restorePending()
    this._refresh()
  }

  // Filter chips as the user types. Empty query shows everything.
  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    this.chipTargets.forEach((chip) => {
      const name = (chip.dataset.name || "").toLowerCase()
      const slug = (chip.dataset.slug || "").toLowerCase()
      const match = !q || name.includes(q) || slug.includes(q)
      chip.style.display = match ? "" : "none"
    })
  }

  toggle(event) {
    const chip = event.currentTarget
    const slug = chip.dataset.slug
    const name = chip.dataset.name
    if (this._selected.has(slug)) {
      this._selected.delete(slug)
      this._setChipSelected(chip, false)
    } else {
      this._selected.set(slug, name)
      this._setChipSelected(chip, true)
    }
    this._refresh()
  }

  remove(event) {
    const slug = event.currentTarget.dataset.slug
    if (this._selected.has(slug)) {
      this._selected.delete(slug)
      const sourceChip = this.chipTargets.find((c) => c.dataset.slug === slug)
      if (sourceChip) this._setChipSelected(sourceChip, false)
      this._refresh()
    }
  }

  // Toggle the chip's selected appearance via real Tailwind classes —
  // works regardless of CSS nesting / data-attribute variant compile state.
  _setChipSelected(chip, selected) {
    chip.dataset.selected = selected ? "true" : "false"
    if (selected) {
      chip.classList.remove("bg-white", "text-amber-900", "border-amber-300", "hover:bg-amber-50")
      chip.classList.add("bg-amber-700", "text-white", "border-amber-700", "hover:bg-amber-800")
    } else {
      chip.classList.remove("bg-amber-700", "text-white", "border-amber-700", "hover:bg-amber-800")
      chip.classList.add("bg-white", "text-amber-900", "border-amber-300", "hover:bg-amber-50")
    }
  }

  async submit() {
    if (this._selected.size === 0) return
    this._clearBanner()
    this._setLoading(true)

    // Network call has its own try/catch so an exception thrown during
    // result rendering doesn't masquerade as "Network error".
    let data, ok
    try {
      const ingredients = Array.from(this._selected.entries()).map(
        ([ slug, name ]) => ({ slug, name })
      )
      const diet = this.hasDietSelectTarget ? this.dietSelectTarget.value : ""
      const payload = { ingredients }
      if (diet) payload.diet = diet
      const res = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify(payload)
      })
      data = await res.json().catch((err) => {
        console.error("[Fridge] response JSON parse failed:", err)
        return {}
      })
      ok = res.ok
    } catch (err) {
      console.error("[Fridge] network error:", err)
      this._showBanner("Network error — please try again.", "error")
      this._setLoading(false)
      return
    }

    this._setLoading(false)

    try {
      if (ok && data.status === "ok") {
        this._clearPending()
        this._renderSuggestions(data)
      } else if (data.reason === "auth_required") {
        this._handleAuthRequired(data)
      } else if (data.status === "rate_limited") {
        this._showBanner(this._rateLimitMessage(data), "warn")
      } else {
        this._showBanner(this._errorMessage(data), "error")
      }
    } catch (err) {
      console.error("[Fridge] error rendering response:", err, data)
      this._showBanner("Got the suggestions but couldn't display them — please refresh and try again.", "error")
    }
  }

  // Save current selection to localStorage and surface a sign-in / sign-up
  // CTA. After auth, the page reloads and connect() restores the chips.
  _handleAuthRequired(data) {
    this._persistPending()
    const signIn = data.sign_in_url || "/users/sign_in"
    const signUp = data.sign_up_url || "/users/sign_up"
    if (!this.hasBannerTarget) return
    this.bannerTarget.innerHTML = `
      <strong>Sign in to use the AI Fridge Tool.</strong>
      It's free with a Breakfast NZ account — and we'll keep your fridge ready while you do.
      <a href="${signIn}" class="underline font-semibold ml-2">Sign in</a>
      <span class="mx-1">·</span>
      <a href="${signUp}" class="underline font-semibold">Create account</a>
    `
    this.bannerTarget.classList.remove("hidden",
      "border-red-200", "bg-red-50", "text-red-700",
      "border-emerald-200", "bg-emerald-50", "text-emerald-700")
    this.bannerTarget.classList.add("border-amber-200", "bg-amber-50", "text-amber-800")
  }

  _persistPending() {
    try {
      const entries = Array.from(this._selected.entries())
      if (entries.length === 0) return
      localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(entries))
    } catch (_e) { /* private mode etc — silently skip */ }
  }

  _restorePending() {
    try {
      const raw = localStorage.getItem(this.constructor.STORAGE_KEY)
      if (!raw) return
      const entries = JSON.parse(raw)
      if (!Array.isArray(entries)) return

      for (const [ slug, name ] of entries) {
        this._selected.set(slug, name)
        const chip = this.chipTargets.find((c) => c.dataset.slug === slug)
        if (chip) this._setChipSelected(chip, true)
      }
    } catch (_e) { /* malformed payload — silently skip */ }
  }

  _clearPending() {
    try { localStorage.removeItem(this.constructor.STORAGE_KEY) } catch (_e) { /* */ }
  }

  // ---- internals ----

  _refresh() {
    this._renderSelected()
    this._renderCounter()
    this.submitTarget.disabled = this._selected.size === 0
  }

  _renderSelected() {
    // Clear existing chips (but keep the empty-state placeholder).
    this.selectedTarget.querySelectorAll("[data-selected-chip]").forEach((n) => n.remove())

    if (this._selected.size === 0) {
      if (this.hasSelectedEmptyTarget) this.selectedEmptyTarget.classList.remove("hidden")
      return
    }
    if (this.hasSelectedEmptyTarget) this.selectedEmptyTarget.classList.add("hidden")

    for (const [ slug, name ] of this._selected) {
      const chip = document.createElement("button")
      chip.type = "button"
      chip.dataset.selectedChip = "true"
      chip.dataset.slug = slug
      chip.setAttribute("data-action", "click->fridge#remove")
      chip.className =
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-700 text-white text-sm hover:bg-amber-800"
      chip.innerHTML = `<span>${escape(name)}</span><span aria-hidden="true">×</span>`
      this.selectedTarget.appendChild(chip)
    }
  }

  _renderCounter() {
    if (!this.hasCounterTarget) return
    const n = this._selected.size
    this.counterTarget.textContent = `${n} ${n === 1 ? "item" : "items"}`
  }

  _renderSuggestions(data) {
    const cards = (data.suggestions || []).map((s) => this._renderCardHTML(s))
    this.suggestionsTarget.innerHTML = cards.join("")
    this.resultsTarget.classList.remove("hidden")

    if (data.cached) {
      this._showBanner("Served from cache — instant.", "info")
    }
    if (typeof this.resultsTarget.scrollIntoView === "function") {
      this.resultsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  // Build card HTML as a string. All interpolated values escaped via
  // `escape()` (defined at bottom of file). Plain Tailwind classes only —
  // no data-attribute variants — so the chip/pill styling can't break
  // because of CSS-compile gaps.
  _renderCardHTML(s) {
    const title = escape(s.title)
    const time = s.time_minutes ? `${escape(String(s.time_minutes))} min` : ""
    const intro = s.intro
      ? `<p class="text-amber-800 mb-4 text-[15px]">${escape(s.intro)}</p>`
      : ""
    const ingredientPills = (s.ingredients_used || [])
      .map((ing) =>
        `<span class="inline-block px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-xs">${escape(ing)}</span>`
      ).join("")
    const steps = (s.recipe_steps || [])
      .map((step, i) =>
        `<li class="leading-relaxed"><strong class="text-amber-700 mr-2">${i + 1}.</strong> ${escape(step)}</li>`
      ).join("")
    const link = s.matched_recipe_url
      ? `<a href="${escape(s.matched_recipe_url)}" class="inline-flex items-center gap-2 text-amber-800 font-semibold text-sm hover:text-amber-950">View the full recipe <span aria-hidden="true">→</span></a>`
      : ""

    return `
      <article class="bg-white rounded-2xl border border-amber-200 p-6 sm:p-7">
        <div class="flex items-baseline justify-between mb-2 gap-3">
          <h3 class="font-display font-semibold text-xl sm:text-2xl text-amber-950 leading-tight">${title}</h3>
          <span class="flex-shrink-0 text-[10px] uppercase tracking-[0.25em] text-amber-700/85">${time}</span>
        </div>
        ${intro}
        <div class="flex flex-wrap gap-1.5 mb-4">${ingredientPills}</div>
        <details class="mb-4">
          <summary class="font-display italic text-amber-700 text-sm cursor-pointer hover:text-amber-900">Method</summary>
          <ol class="mt-3 space-y-2 text-[14px] text-amber-900">${steps}</ol>
        </details>
        ${link}
      </article>
    `
  }

  _setLoading(on) {
    this.loadingTarget.classList.toggle("hidden", !on)
    this.submitTarget.disabled = on || this._selected.size === 0
  }

  _showBanner(message, kind) {
    if (!this.hasBannerTarget) return
    this.bannerTarget.textContent = message
    this.bannerTarget.classList.remove("hidden",
      "border-red-200", "bg-red-50", "text-red-700",
      "border-amber-200", "bg-amber-50", "text-amber-800",
      "border-emerald-200", "bg-emerald-50", "text-emerald-700")
    if (kind === "error") {
      this.bannerTarget.classList.add("border-red-200", "bg-red-50", "text-red-700")
    } else if (kind === "warn") {
      this.bannerTarget.classList.add("border-amber-200", "bg-amber-50", "text-amber-800")
    } else {
      this.bannerTarget.classList.add("border-emerald-200", "bg-emerald-50", "text-emerald-700")
    }
  }

  _clearBanner() {
    if (!this.hasBannerTarget) return
    this.bannerTarget.classList.add("hidden")
    this.bannerTarget.textContent = ""
  }

  _rateLimitMessage(data) {
    if (data.reason === "killswitch_tripped") {
      return "We've hit today's platform-wide budget. Try again tomorrow — sorry."
    }
    if (data.reason === "account_quota_exceeded") {
      return "You've used today's queries. Resets at midnight, or upgrade for more."
    }
    return "Daily limit reached. Try again tomorrow."
  }

  _errorMessage(data) {
    if (data.reason === "no_ingredients") return "Add at least one ingredient first."
    if (data.reason === "llm_invalid") return "We couldn't make sense of that combination — try a different mix."
    return "Something went wrong — please try again."
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this._csrfToken()
    }
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}

function escape(s) {
  const div = document.createElement("div")
  div.textContent = s == null ? "" : String(s)
  return div.innerHTML
}
